<!-- Module Documents Widget - Professional ERP Implementation -->
<div class="document-widget-container"
  (dragenter)="onDragEnter($event)"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onDrop($event)">

  <!-- DRAG & DROP OVERLAY (opt-in, matches Task Attachments) -->
  @if (isDragging && canDropFiles()) {
    <div class="drag-overlay" @slideInFromTop role="region" aria-label="Drag and drop upload area">
      <div class="upload-indicator">
        @if (draggedFileCount > 0) {
          <div class="file-count-badge">
            {{ draggedFileCount }} file{{ draggedFileCount > 1 ? 's' : '' }}
          </div>
        }
        <div class="upload-icon-circle">
          <i class="fas fa-cloud-upload-alt upload-icon"></i>
        </div>
        <h3 class="upload-title">Drop files to upload</h3>
        <p class="upload-subtitle">Release to upload {{ draggedFileCount }} file(s)</p>
      </div>
    </div>
  }

  <!-- Header Section — suppressed when the host sets [hideHeader] -->
  @if (!hideHeader) {
    <div class="widget-header">
      <div class="header-content">
        <div class="header-icon-wrapper">
          <mat-icon class="header-icon">folder</mat-icon>
        </div>
        <div class="header-info">
          <h3 class="header-title">Documents</h3>
          <p class="header-subtitle">
            {{ documents.length }} documents • Last updated {{ lastUpdated | date:'shortDate' }}
          </p>
        </div>
      </div>
      <div class="header-actions">
        <button mat-icon-button class="view-toggle-button"
          [matTooltip]="viewMode === 'list' ? 'Switch to grid view' : 'Switch to list view'" (click)="toggleViewMode()">
          @if (viewMode === 'list') {
            <mat-icon class="view-icon">view_module</mat-icon>
          }
          @if (viewMode === 'grid') {
            <mat-icon class="view-icon">view_list</mat-icon>
          }
        </button>
        <button mat-icon-button class="expand-toggle-button" (click)="toggleExpanded()">
          <mat-icon [class.rotated]="!isExpanded">expand_more</mat-icon>
        </button>
      </div>
    </div>
  }

  <!-- UPLOAD PROGRESS PANEL (ported from Task Attachments) — sits under the
       header so an in-flight upload appears inside the widget, not above it. -->
  @if (uploadQueue.length > 0) {
    <div class="upload-progress-panel" @slideInFromTop>
      <div class="upload-panel-header">
        <div class="header-info">
          <i class="fas fa-upload"></i>
          <span class="upload-count">
            Uploading {{ getUploadingCount() }} of {{ uploadQueue.length }} files
          </span>
        </div>
        <div class="header-actions">
          <button type="button" class="btn-icon" (click)="toggleUploadPanel()">
            <i class="fas" [ngClass]="isUploadPanelOpen ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </button>
          @if (hasCompletedUploads()) {
            <button type="button" class="btn-icon" (click)="clearCompleted()">
              <i class="fas fa-check"></i>
            </button>
          }
          <button type="button" class="btn-icon" (click)="clearAll()">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
      @if (isUploadPanelOpen) {
        <div class="upload-file-list">
          @for (file of uploadProgress; track file.id) {
            <div class="upload-file-item"
              [ngClass]="{
                'upload-status-pending': file.status === 'pending',
                'upload-status-uploading': file.status === 'uploading',
                'upload-status-complete': file.status === 'complete',
                'upload-status-error': file.status === 'error',
                'upload-status-cancelled': file.status === 'cancelled'
              }">
              <div class="upload-file-preview">
                @if (file.preview) {
                  <img [src]="file.preview" [alt]="file.fileName">
                } @else {
                  <i class="fas fa-file upload-file-icon"></i>
                }
              </div>
              <div class="upload-file-info">
                <div class="upload-file-name" [title]="file.fileName">{{ file.fileName }}</div>
                <div class="upload-file-details">
                  <span class="upload-file-size">{{ file.fileSize }}</span>
                  @switch (file.status) {
                    @case ('pending') {
                      <span class="upload-file-status"><i class="fas fa-clock"></i>Waiting...</span>
                    }
                    @case ('uploading') {
                      <span class="upload-file-status">
                        <i class="fas fa-spinner fa-spin"></i>
                        {{ file.progress }}% • {{ formatUploadSpeed(file.uploadSpeed || 0) }}
                        @if (file.timeRemaining) {
                          <span> • {{ formatTimeRemaining(file.timeRemaining) }}</span>
                        }
                      </span>
                    }
                    @case ('complete') {
                      <span class="upload-file-status upload-status-success">
                        <i class="fas fa-check-circle"></i>Complete
                      </span>
                    }
                    @case ('error') {
                      <span class="upload-file-status upload-status-error">
                        <i class="fas fa-exclamation-circle"></i>{{ file.error || 'Failed' }}
                      </span>
                    }
                    @case ('cancelled') {
                      <span class="upload-file-status upload-status-cancelled">
                        <i class="fas fa-ban"></i>Cancelled
                      </span>
                    }
                  }
                </div>
                @if (file.status === 'uploading') {
                  <div class="upload-file-progress">
                    <div class="progress">
                      <div class="progress-bar" [style.width.%]="file.progress"></div>
                    </div>
                  </div>
                }
              </div>
              <div class="upload-file-actions">
                @if (file.status === 'uploading') {
                  <button class="btn-icon btn-icon-danger" (click)="cancelUpload(file)">
                    <i class="fas fa-times"></i>
                  </button>
                }
                @if (file.status === 'error') {
                  <button class="btn-icon btn-icon-primary" (click)="retryUpload(file)">
                    <i class="fas fa-redo"></i>
                  </button>
                }
                @if (file.status === 'complete' || file.status === 'error' || file.status === 'cancelled') {
                  <button class="btn-icon" (click)="removeFromQueue(file)">
                    <i class="fas fa-trash"></i>
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- Content Area -->
  @if (isExpanded) {
    <div class="widget-content" [@expandCollapse]>
      <!-- Breadcrumb Navigation -->
      @if (folderPath.length > 0) {
        <div class="breadcrumb-navigation">
          <div class="breadcrumb-container">
            <button class="breadcrumb-item" (click)="navigateToBreadcrumb(null)">
              <mat-icon class="breadcrumb-icon">home</mat-icon>
              <span>Home</span>
            </button>
            <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
            @for (folder of folderPath; track folder; let last = $last) {
              <button class="breadcrumb-item" [class.current]="last" (click)="!last && navigateToBreadcrumb(folder.id)">
                <mat-icon class="breadcrumb-icon">folder</mat-icon>
                <span>{{ folder.name }}</span>
              </button>
              @if (!last) {
                <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
              }
            }
          </div>
        </div>
      }
      <!-- Controls Section -->
      <div class="controls-section">
        <div class="controls-wrapper">
          <!-- Top Controls: Search and Action Buttons grouped together -->
          <div class="top-controls">
            <!-- Back Button (only shown when inside subfolders) -->
            @if (shouldShowBackButton()) {
              <button mat-icon-button class="back-button" (click)="goBackToPreviousFolder()"
                matTooltip="Go back">
                <mat-icon>arrow_back</mat-icon>
              </button>
            }
            <!-- Search Input -->
            <div class="search-container">
              <mat-icon class="search-icon">search</mat-icon>
              <input type="text" class="search-input" placeholder="Search documents..." [(ngModel)]="searchQuery"
                (ngModelChange)="onSearchChange()">
            </div>
            <!-- Warehouse-only: filter by source delivery -->
            @if (enableDeliveryFilter && deliveryFilterOptions.length) {
              <div class="delivery-filter">
                <mat-icon class="search-icon">local_shipping</mat-icon>
                <select class="delivery-filter-select" [(ngModel)]="selectedDeliveryCode"
                  (ngModelChange)="applyDeliveryFilter()">
                  <option value="">All deliveries</option>
                  @for (code of deliveryFilterOptions; track code) {
                    <option [value]="code">{{ code }}</option>
                  }
                </select>
              </div>
            }
            <!-- Action Buttons -->
            <div class="action-buttons">
              <!-- <button class="advanced-filter-button" (click)="openAdvancedFilters()">
              <mat-icon class="button-icon">tune</mat-icon>
            </button> -->
            <button class="upload-button" (click)="uploadDocument()" [disabled]="isLocked || !canUploadDocument()" [appPermission]="createPermission" [ngClass]="{'permission-disabled': isLocked || !canUploadDocument()}" >
              <mat-icon class="button-icon">upload</mat-icon>
            </button>
          </div>
        </div>
        <!-- Folder Navigation Tabs with Scrolling -->
        <div class="folder-navigation">
          <div class="folder-tabs-container">
            <!-- Left scroll button -->
            <button mat-icon-button class="folder-scroll-btn folder-scroll-left" [class.disabled]="!canScrollLeft()"
              [disabled]="!canScrollLeft()" (click)="scrollFoldersLeft()" matTooltip="Previous folders">
              <mat-icon>chevron_left</mat-icon>
            </button>
            <!-- Folder tabs viewport -->
            <div #folderViewport class="folder-tabs-viewport">
              <div #folderTabsContainer class="folder-tabs" [style.transform]="getFolderTabsTransform()">
                @for (folder of folders; track folder) {
                  <button class="folder-tab" [class.active]="currentFolder === folder.id"
                    (click)="selectFolder(folder.id)">
                    <mat-icon class="folder-icon">{{ folder.id === 0 ? 'folder_open' : 'folder' }}</mat-icon>
                    <span class="folder-name">{{ folder.name }}</span>
                    <span class="folder-count">{{ folder.count }}</span>
                  </button>
                }
              </div>
            </div>
            <!-- Right scroll button -->
            <button mat-icon-button class="folder-scroll-btn folder-scroll-right" [class.disabled]="!canScrollRight()"
              [disabled]="!canScrollRight()" (click)="scrollFoldersRight()" matTooltip="More folders">
              <mat-icon>chevron_right</mat-icon>
            </button>
            <!-- Create folder button -->
            <button mat-icon-button class="create-folder-btn" (click)="createNewFolder()" matTooltip="Create folder" [disabled]="isLocked" [appPermission]="createFolderPermission" [ngClass]="{'permission-disabled': isLocked}">
              <mat-icon>create_new_folder</mat-icon>
            </button>
          </div>
        </div>
        <!-- Filter Buttons
        <div class="filter-buttons">
          <button class="filter-button"
            [class.active]="activeFilter === 'all'"
            (click)="setFilter('all')">
            All Documents
          </button>
          <button class="filter-button"
            [class.active]="activeFilter === 'proposal'"
            (click)="setFilter('proposal')">
            <mat-icon class="button-icon">description</mat-icon>
            Proposals
          </button>
          <button class="filter-button"
            [class.active]="activeFilter === 'contract'"
            (click)="setFilter('contract')">
            <mat-icon class="button-icon">assignment</mat-icon>
            Contracts
          </button>
          <button class="filter-button"
            [class.active]="activeFilter === 'invoice'"
            (click)="setFilter('invoice')">
            <mat-icon class="button-icon">receipt</mat-icon>
            Invoices
          </button>
          <button class="filter-button"
            [class.active]="activeFilter === 'other'"
            (click)="setFilter('other')">
            <mat-icon class="button-icon">folder</mat-icon>
            Other
          </button>
        </div>
      </div> -->
      <!-- Selected Documents Bar -->
      @if (selectedDocuments.length > 0 || canPaste()) {
        <div class="selected-bar" [@slideIn]>
          @if (selectedDocuments.length > 0) {
            <div class="selected-info">
              <span class="selected-count">{{ selectedDocuments.length }}</span>
              document(s) selected
            </div>
          }
          <div class="bulk-actions">
            @if (selectedDocuments.length > 0) {
              <button class="bulk-action-button"
                (click)="handleBulkAction('download')" [disabled]="selectedDocuments.length !== 1"
                [appPermission]="downloadPermission"
                matTooltip="Download selected document (single item only)">
                <mat-icon class="button-icon">download</mat-icon>
                Download
              </button>
            }
            @if (selectedDocuments.length > 0) {
              <button class="bulk-action-button" (click)="handleCopySelected()"
                [disabled]="!hasItemsSelected() || isLocked" [ngClass]="{'permission-disabled': isLocked}" matTooltip="Copy selected document(s)">
                <mat-icon class="button-icon">content_copy</mat-icon>
                Copy
              </button>
            }
            @if (selectedDocuments.length > 0) {
              <button class="bulk-action-button" (click)="handleCutSelected()"
                [disabled]="!hasItemsSelected() || isLocked" [ngClass]="{'permission-disabled': isLocked}" matTooltip="Cut selected document(s) for moving">
                <mat-icon class="button-icon">content_cut</mat-icon>
                Cut
              </button>
            }
            <button class="bulk-action-button" [class.highlight]="canPaste()" (click)="pasteItem()"
              [disabled]="!canPaste() || isLocked" [ngClass]="{'permission-disabled': isLocked}" matTooltip="Paste copied/cut document(s) to current folder">
              <mat-icon class="button-icon">content_paste</mat-icon>
              Paste
              @if (canPaste()) {
                <span style="margin-left: 4px; font-size: 11px;">
                  ({{ clipboardAction === 'copy' ? 'Copy' : 'Cut' }}: {{ getClipboardDisplayText() }})
                </span>
              }
            </button>
            @if (selectedDocuments.length > 0) {
              <button class="bulk-action-button" (click)="handleBulkAction('share')"
                [appPermission]="sharePermission"
                [disabled]="selectedDocuments.length !== 1" matTooltip="Share document (single item only)">
                <mat-icon class="button-icon">share</mat-icon>
                Share
              </button>
            }
            @if (selectedDocuments.length > 0) {
              <button class="bulk-action-button" (click)="handleBulkAction('delete')"
                [appPermission]="deletePermission"
                [disabled]="!hasItemsSelected() || isLocked" [ngClass]="{'permission-disabled': isLocked}" matTooltip="Delete selected document(s)">
                <mat-icon class="button-icon">delete</mat-icon>
                Delete
              </button>
            }
            <button class="bulk-action-button cancel"
              (click)="clearSelection(); clipboardItems = []; clipboardItem = null; clipboardAction = null;">
              <mat-icon class="button-icon">close</mat-icon>
              Close
            </button>
          </div>
        </div>
      }
    </div>
    <!-- Documents Section -->
    <div class="documents-section">
      <!-- Sort Bar -->
      @if (filteredDocuments.length > 0) {
        <div class="sort-bar">
          <div class="results-info">
            Showing {{ filteredDocuments.length }} of {{ documents.length }} documents
          </div>
          <div class="sort-controls">
            <span class="sort-label">Sort by:</span>
            <button class="sort-button" [class.active]="sortBy === 'date'" (click)="toggleSort('date')">
              Date
              @if (sortBy === 'date') {
                <mat-icon class="sort-icon">
                  {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              }
            </button>
            <button class="sort-button" [class.active]="sortBy === 'name'" (click)="toggleSort('name')">
              Name
              @if (sortBy === 'name') {
                <mat-icon class="sort-icon">
                  {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              }
            </button>
            <button class="sort-button" [class.active]="sortBy === 'size'" (click)="toggleSort('size')">
              Size
              @if (sortBy === 'size') {
                <mat-icon class="sort-icon">
                  {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              }
            </button>
          </div>
        </div>
      }
      <!-- Initial 5s warmup: queue job hasn't had time to run yet -->
      @if (isInitialPreparing) {
        <div class="loading-state">
          <div class="loading-spinner">
            <mat-spinner diameter="40"></mat-spinner>
          </div>
          <p class="loading-text">Preparing...</p>
        </div>
      }
      <!-- Loading State -->
      @if (isLoading && !isLoadingMore && !isInitialPreparing) {
        <div class="loading-state">
          <div class="loading-spinner">
            <mat-spinner diameter="40"></mat-spinner>
          </div>
          <p class="loading-text">Loading documents...</p>
        </div>
      }
      <!-- Document List View -->
      @if (viewMode === 'list' && !isLoading && filteredDocuments.length > 0) {
        <div class="document-list"
          [style.height]="maxContentHeight || null" [style.flex]="maxContentHeight ? '0 0 auto' : null" [style.overflow-y]="maxContentHeight ? 'auto' : null">
          @for (doc of sortedDocuments; track doc) {
            <div class="document-item" [@fadeIn] (click)="handleDocumentClick(doc)"
              style="cursor: pointer">
              <div class="document-checkbox">
                <mat-checkbox class="bidone-checkbox" [checked]="isDocumentSelected(doc.id.toString())"
                  (change)="toggleDocumentSelection(doc.id.toString())" (click)="$event.stopPropagation()">
                </mat-checkbox>
              </div>
              <div class="document-icon-wrapper document-thumbnail-wrapper">
                <app-file-thumbnail
                  size="sm"
                  [showExtension]="false"
                  [itemType]="doc.type || 'file'"
                  [src]="getThumbnailSrc(doc)"
                  [hasThumbnail]="hasGeneratedThumbnail(doc)"
                  [videoSrc]="doc.file_url || doc.url"
                  [fileName]="doc.original_name"
                  [fileType]="doc.file_type"
                  [mimeType]="doc.mime_type || ''">
                </app-file-thumbnail>
              </div>
              <div class="document-details" (contextmenu)="onRightClick($event, doc)">
                <div class="document-header">
                  <div class="document-title-row">
                    <h4 class="document-name" [class.folder-name]="doc.type === 'folder'">
                      {{ doc.original_name }}
                    </h4>
                    <!-- Favorite/Star functionality commented out
                    <button mat-icon-button
                      class="star-button"
                      [class.starred]="doc.isStarred"
                      (click)="toggleStar(doc.id.toString()); $event.stopPropagation()">
                      <mat-icon>{{ doc.isStarred ? 'star' : 'star_border' }}</mat-icon>
                    </button>
                    -->
                  </div>
                  <div class="document-date">
                    <mat-icon class="date-icon">calendar_today</mat-icon>
                    {{ doc.formattedDate }}
                  </div>
                </div>
                <div class="document-metadata">
                  <div class="meta-item">
                    <mat-icon class="meta-icon">description</mat-icon>
                    {{ doc.displaySize }}
                  </div>
                  <div class="meta-item">
                    <mat-icon class="meta-icon">person</mat-icon>
                    {{ doc.ownerName }}
                  </div>
                  <div class="meta-item">
                    <span class="category-badge">
                      <mat-icon class="category-icon">label</mat-icon>
                      {{ doc.documentCategory | titlecase }}
                    </span>
                  </div>
                  <div class="document-actions">
                    @if (doc.type !== 'folder') {
                      <button mat-icon-button class="action-button preview"
                        [disabled]="!docPerms.preview" [ngClass]="{'permission-disabled': !docPerms.preview}"
                        matTooltip="Preview Document" (click)="previewDocument(doc); $event.stopPropagation()">
                        <mat-icon>visibility</mat-icon>
                      </button>
                    }
                    @if (doc.type !== 'folder') {
                      <button mat-icon-button class="action-button download"
                        [disabled]="!docPerms.download" [ngClass]="{'permission-disabled': !docPerms.download}"
                        matTooltip="Download Document" (click)="downloadDocument(doc); $event.stopPropagation()">
                        <mat-icon>download</mat-icon>
                      </button>
                    }
                    @if (doc.type === 'folder') {
                      <button mat-icon-button class="action-button open"
                        matTooltip="Open Folder" (click)="openFolder(doc); $event.stopPropagation()">
                        <mat-icon>folder_open</mat-icon>
                      </button>
                    }
                    <button mat-icon-button class="action-button share" matTooltip="Share Document"
                      [disabled]="!docPerms.share" [ngClass]="{'permission-disabled': !docPerms.share}"
                      (click)="shareDocument(doc); $event.stopPropagation()">
                      <mat-icon>share</mat-icon>
                    </button>
                    <button mat-icon-button class="action-button more" matTooltip="More Options"
                      (click)="$event.stopPropagation()" [matMenuTriggerFor]="documentMenu">
                      <mat-icon>more_vert</mat-icon>
                    </button>
                    <mat-menu #documentMenu="matMenu">
                      <button mat-menu-item [appPermission]="renamePermission">
                        <mat-icon>edit</mat-icon>
                        <span>Rename</span>
                      </button>
                      <button mat-menu-item [appPermission]="duplicatePermission">
                        <mat-icon>content_copy</mat-icon>
                        <span>Duplicate</span>
                      </button>
                      <button mat-menu-item [appPermission]="archivePermission">
                        <mat-icon>archive</mat-icon>
                        <span>Archive</span>
                      </button>
                      <mat-divider></mat-divider>
                      <button mat-menu-item class="delete-option" (click)="deleteDocument(doc)" [disabled]="isLocked" [appPermission]="deletePermission" [ngClass]="{'permission-disabled': isLocked}">
                        <mat-icon>delete</mat-icon>
                        <span>Delete</span>
                      </button>
                    </mat-menu>
                  </div>
                </div>
              </div>
            </div>
          }
          <!-- Load More Button -->
          @if (showLoadMoreOption) {
            <div class="load-more-section">
              <button mat-button class="load-more-button" (click)="loadMore()" [disabled]="isLoadingMore">
                @if (isLoadingMore) {
                  <mat-spinner diameter="20"></mat-spinner>
                }
                @if (!isLoadingMore) {
                  <span>Load More Documents</span>
                }
                @if (isLoadingMore) {
                  <span>Loading...</span>
                }
              </button>
            </div>
          }
        </div>
      }
      <!-- Document Grid View -->
      @if (viewMode === 'grid' && !isLoading && filteredDocuments.length > 0) {
        <div class="document-grid"
          [style.height]="maxContentHeight || null" [style.flex]="maxContentHeight ? '0 0 auto' : null" [style.overflow-y]="maxContentHeight ? 'auto' : null">
          @for (doc of sortedDocuments; track doc) {
            <div class="document-card" [@fadeIn] (click)="handleDocumentClick(doc)"
              (contextmenu)="onRightClick($event, doc)">
              <div class="card-header">
                <mat-checkbox [checked]="isDocumentSelected(doc.id.toString())"
                  (change)="toggleDocumentSelection(doc.id.toString())" (click)="$event.stopPropagation()"
                  class="card-checkbox bidone-checkbox">
                </mat-checkbox>
                <!-- Favorite/Star functionality commented out
                <button mat-icon-button
                  class="star-button"
                  [class.starred]="doc.isStarred"
                  (click)="toggleStar(doc.id.toString()); $event.stopPropagation()">
                  <mat-icon>{{ doc.isStarred ? 'star' : 'star_border' }}</mat-icon>
                </button>
                -->
              </div>
              <div class="card-preview">
                <!-- Thumbnail: real preview for images/videos, file-type tile
                     for everything else, folder tile for folders. -->
                <app-file-thumbnail
                  size="md"
                  [itemType]="doc.type || 'file'"
                  [src]="getThumbnailSrc(doc)"
                  [hasThumbnail]="hasGeneratedThumbnail(doc)"
                  [videoSrc]="doc.file_url || doc.url"
                  [fileName]="doc.original_name"
                  [fileType]="doc.file_type"
                  [mimeType]="doc.mime_type || ''">
                </app-file-thumbnail>
                <!-- User avatar overlay (only for files, not folders) -->
                @if (doc.type !== 'folder') {
                  <div class="thumbnail-avatar" [title]="doc.ownerName"
                    [style.background]="!doc.ownerAvatar ? getAvatarColor(doc.ownerName || '') : ''">
                    @if (doc.ownerAvatar) {
                      <img [src]="doc.ownerAvatar" [alt]="doc.ownerName"
                        (error)="doc.ownerAvatar = ''">
                    }
                    @if (!doc.ownerAvatar) {
                      <span class="avatar-initials">
                        {{ getInitials(doc.ownerName) }}
                      </span>
                    }
                  </div>
                }
                <!-- Overlay actions (only for non-folders) -->
                @if (doc.type !== 'folder') {
                  <div class="card-overlay">
                    <div class="overlay-actions">
                      <button mat-icon-button class="overlay-button"
                        [disabled]="!docPerms.preview" [ngClass]="{'permission-disabled': !docPerms.preview}"
                        (click)="previewDocument(doc); $event.stopPropagation()">
                        <mat-icon>visibility</mat-icon>
                      </button>
                      <button mat-icon-button class="overlay-button"
                        [disabled]="!docPerms.download" [ngClass]="{'permission-disabled': !docPerms.download}"
                        (click)="downloadDocument(doc); $event.stopPropagation()">
                        <mat-icon>download</mat-icon>
                      </button>
                      <button mat-icon-button class="overlay-button" [disabled]="!docPerms.share" [ngClass]="{'permission-disabled': !docPerms.share}" (click)="shareDocument(doc); $event.stopPropagation()">
                        <mat-icon>share</mat-icon>
                      </button>
                    </div>
                  </div>
                }
              </div>
              <div class="card-content">
                <div class="card-title-row">
                  <mat-icon class="type-icon">{{ doc.type === 'folder' ? 'folder' : getDocumentIcon(doc.file_type)
                  }}</mat-icon>
                  <h4 class="card-title" [class.folder-title]="doc.type === 'folder'">{{ doc.original_name }}</h4>
                </div>
                <div class="card-meta">
                  <span class="meta-size">{{ doc.displaySize }}</span>
                  <span class="meta-date">{{ doc.formattedDate }}</span>
                </div>
                <div class="card-footer">
                  <span class="category-tag">{{ doc.documentCategory | titlecase }}</span>
                </div>
              </div>
            </div>
          }
        </div>
      }
      <!-- Empty State -->
      @if (!isLoading && !isInitialPreparing && filteredDocuments.length === 0) {
        <div class="empty-state">
          <div class="empty-icon-wrapper">
            <mat-icon class="empty-icon">folder_open</mat-icon>
          </div>
          <h3 class="empty-title">No documents found</h3>
          <p class="empty-description">
            @if (searchQuery || activeFilter !== 'all') {
              No documents match your current filter criteria. Try adjusting your filters or upload a new document.
            } @else {
              No documents have been uploaded yet. Upload a document to get started.
            }
          </p>
          <div class="empty-actions">
            @if (searchQuery || activeFilter !== 'all') {
              <button class="reset-button" (click)="resetFilters()">
                Reset Filters
              </button>
            }
            <button class="upload-button-empty" (click)="uploadDocument()" [disabled]="isLocked || !canUploadDocument()" [appPermission]="createPermission" [ngClass]="{'permission-disabled': isLocked || !canUploadDocument()}">
              <mat-icon class="button-icon">upload</mat-icon>
              Upload Document
            </button>
          </div>
        </div>
      }
      <!-- Footer -->
      @if (filteredDocuments.length > 0 && !isLoading) {
        <div class="widget-footer">
          <button class="add-more-button" (click)="uploadDocument()" [disabled]="isLocked || !canUploadDocument()" [appPermission]="createPermission" [ngClass]="{'permission-disabled': isLocked || !canUploadDocument()}">
            <mat-icon class="button-icon">add</mat-icon>
            Add More Documents
          </button>
          <!-- <button class="view-all-button">
          View All Documents
          <mat-icon class="button-icon">open_in_new</mat-icon>
        </button> -->
      </div>
    }
  </div>
</div>
<!-- Context Menu -->
@if (contextMenuVisible) {
  <div class="context-menu" [style.left.px]="contextMenuX" [style.top.px]="contextMenuY"
    (click)="$event.stopPropagation()">
    <div class="context-menu-content">
      <!-- Folder Context Menu -->
      @if (contextMenuItem?.type === 'folder') {
        <button class="context-menu-item" (click)="handleContextMenuAction('open')">
          <mat-icon>folder_open</mat-icon>
          <span>Open</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('newFolder')">
          <mat-icon>create_new_folder</mat-icon>
          <span>New Folder</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('uploadHere')" [appPermission]="createPermission" [disabled]="!canUploadDocument()">
          <mat-icon>upload</mat-icon>
          <span>Upload Files Here</span>
        </button>
        <mat-divider></mat-divider>
        <button class="context-menu-item" (click)="handleContextMenuAction('rename')" [appPermission]="renamePermission">
          <mat-icon>edit</mat-icon>
          <span>Rename</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('copy')">
          <mat-icon>content_copy</mat-icon>
          <span>Copy</span>
        </button>
        <button class="context-menu-item" [disabled]="!clipboardItem" (click)="handleContextMenuAction('paste')">
          <mat-icon>content_paste</mat-icon>
          <span>Paste</span>
        </button>
        <mat-divider></mat-divider>
        <button class="context-menu-item delete" (click)="handleContextMenuAction('delete')" [appPermission]="deletePermission">
          <mat-icon>delete</mat-icon>
          <span>Delete</span>
        </button>
      }
      <!-- File Context Menu -->
      @if (contextMenuItem?.type !== 'folder') {
        <button class="context-menu-item" (click)="handleContextMenuAction('open')" [appPermission]="previewPermission">
          <mat-icon>visibility</mat-icon>
          <span>Open/Preview</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('download')" [appPermission]="downloadPermission">
          <mat-icon>download</mat-icon>
          <span>Download</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('share')" [appPermission]="sharePermission">
          <mat-icon>share</mat-icon>
          <span>Share</span>
        </button>
        <mat-divider></mat-divider>
        <button class="context-menu-item" (click)="handleContextMenuAction('rename')" [appPermission]="renamePermission">
          <mat-icon>edit</mat-icon>
          <span>Rename</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('copy')">
          <mat-icon>content_copy</mat-icon>
          <span>Copy</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('cut')">
          <mat-icon>content_cut</mat-icon>
          <span>Cut</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('duplicate')" [appPermission]="duplicatePermission">
          <mat-icon>control_point_duplicate</mat-icon>
          <span>Duplicate</span>
        </button>
        <button class="context-menu-item" (click)="handleContextMenuAction('archive')" [appPermission]="archivePermission">
          <mat-icon>archive</mat-icon>
          <span>Archive</span>
        </button>
        <mat-divider></mat-divider>
        <button class="context-menu-item delete" (click)="handleContextMenuAction('delete')" [appPermission]="deletePermission">
          <mat-icon>delete</mat-icon>
          <span>Delete</span>
        </button>
      }
    </div>
  </div>
}
</div>
}